import axios, { AxiosInstance, AxiosError } from 'axios';
import { authService } from './auth';
import type {
  User,
  Item,
  ItemCreate,
  ItemUpdate,
  ItemFilters,
  ItemSort,
  Comment,
  CommentCreate,
  Project,
  AppConfig,
  BrandingConfig,
  Integration,
  GitHubConfig,
  SupportTicket,
  SupportTicketCreate,
  PaginatedResponse,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || '/api';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use(async (config) => {
      const token = await authService.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token expired or invalid, redirect to login
          authService.logout();
        }
        return Promise.reject(error);
      }
    );
  }

  // =============================================================================
  // User Endpoints
  // =============================================================================

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get('/users/me');
    return response.data;
  }

  async getUsers(): Promise<User[]> {
    const response = await this.client.get('/users');
    return response.data;
  }

  async getUser(id: string): Promise<User> {
    const response = await this.client.get(`/users/${id}`);
    return response.data;
  }

  // Admin: sync users from identity provider
  async syncUsersPreview(): Promise<{ to_add: any[]; to_update: any[]; to_deactivate: any[] }> {
    const response = await this.client.post('/admin/users/sync/preview');
    return response.data;
  }

  async syncUsers(): Promise<{ added: number; updated: number; deactivated: number }> {
    const response = await this.client.post('/admin/users/sync');
    return response.data;
  }

  // =============================================================================
  // Item Endpoints (Requirements, Feedback, Bugs)
  // =============================================================================

  async getItems(
    filters?: ItemFilters,
    sort?: ItemSort,
    page = 1,
    pageSize = 20
  ): Promise<PaginatedResponse<Item>> {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('page_size', pageSize.toString());

    if (filters) {
      if (filters.type) params.set('type', filters.type);
      if (filters.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        statuses.forEach(s => params.append('status', s));
      }
      if (filters.priority) {
        const priorities = Array.isArray(filters.priority) ? filters.priority : [filters.priority];
        priorities.forEach(p => params.append('priority', p));
      }
      if (filters.assigned_to) params.set('assigned_to', filters.assigned_to);
      if (filters.created_by) params.set('created_by', filters.created_by);
      if (filters.project_id) params.set('project_id', filters.project_id);
      if (filters.tags) {
        filters.tags.forEach(t => params.append('tag', t));
      }
      if (filters.search) params.set('search', filters.search);
    }

    if (sort) {
      params.set('sort_by', sort.field);
      params.set('sort_direction', sort.direction);
    }

    const response = await this.client.get(`/items?${params.toString()}`);
    return response.data;
  }

  async getItem(id: string): Promise<Item> {
    const response = await this.client.get(`/items/${id}`);
    return response.data;
  }

  async createItem(data: ItemCreate): Promise<Item> {
    const response = await this.client.post('/items', data);
    return response.data;
  }

  async updateItem(id: string, data: ItemUpdate): Promise<Item> {
    const response = await this.client.patch(`/items/${id}`, data);
    return response.data;
  }

  async deleteItem(id: string): Promise<void> {
    await this.client.delete(`/items/${id}`);
  }

  // Voting
  async voteForItem(id: string): Promise<Item> {
    const response = await this.client.post(`/items/${id}/vote`);
    return response.data;
  }

  async removeVote(id: string): Promise<Item> {
    const response = await this.client.delete(`/items/${id}/vote`);
    return response.data;
  }

  async hasVoted(id: string): Promise<boolean> {
    const response = await this.client.get(`/items/${id}/vote`);
    return response.data.has_voted;
  }

  // =============================================================================
  // Comment Endpoints
  // =============================================================================

  async getComments(itemId: string): Promise<Comment[]> {
    const response = await this.client.get(`/items/${itemId}/comments`);
    return response.data;
  }

  async createComment(itemId: string, data: CommentCreate): Promise<Comment> {
    const response = await this.client.post(`/items/${itemId}/comments`, data);
    return response.data;
  }

  async deleteComment(itemId: string, commentId: string): Promise<void> {
    await this.client.delete(`/items/${itemId}/comments/${commentId}`);
  }

  // =============================================================================
  // Project Endpoints
  // =============================================================================

  async getProjects(): Promise<Project[]> {
    const response = await this.client.get('/projects');
    return response.data;
  }

  async getProject(id: string): Promise<Project> {
    const response = await this.client.get(`/projects/${id}`);
    return response.data;
  }

  async createProject(data: { name: string; description?: string }): Promise<Project> {
    const response = await this.client.post('/projects', data);
    return response.data;
  }

  async updateProject(id: string, data: { name?: string; description?: string; status?: string }): Promise<Project> {
    const response = await this.client.patch(`/projects/${id}`, data);
    return response.data;
  }

  async deleteProject(id: string): Promise<void> {
    await this.client.delete(`/projects/${id}`);
  }

  // =============================================================================
  // Configuration Endpoints
  // =============================================================================

  async getPublicConfig(): Promise<BrandingConfig> {
    const response = await this.client.get('/config/public');
    return response.data;
  }

  async getConfig(): Promise<AppConfig> {
    const response = await this.client.get('/admin/config');
    return response.data;
  }

  async updateConfig(updates: Partial<AppConfig>): Promise<AppConfig> {
    const response = await this.client.patch('/admin/config', updates);
    return response.data;
  }

  // =============================================================================
  // Integration Endpoints
  // =============================================================================

  async getIntegrations(): Promise<Integration[]> {
    const response = await this.client.get('/admin/integrations');
    return response.data;
  }

  async getIntegration(type: string): Promise<Integration> {
    const response = await this.client.get(`/admin/integrations/${type}`);
    return response.data;
  }

  async updateIntegration(type: string, config: Record<string, unknown>): Promise<Integration> {
    const response = await this.client.patch(`/admin/integrations/${type}`, config);
    return response.data;
  }

  async testIntegration(type: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post(`/admin/integrations/${type}/test`);
    return response.data;
  }

  // GitHub specific
  async getGitHubConfig(): Promise<GitHubConfig> {
    const response = await this.client.get('/admin/integrations/github');
    return response.data;
  }

  async updateGitHubConfig(config: GitHubConfig): Promise<GitHubConfig> {
    const response = await this.client.patch('/admin/integrations/github', config);
    return response.data;
  }

  // =============================================================================
  // SSO Configuration Endpoints
  // =============================================================================

  async getSsoStatus(): Promise<{
    active_provider: string | null;
    cognito_entity_id: string;
    cognito_acs_url: string;
    providers: Record<string, { configured: boolean; enabled: boolean }>;
  }> {
    const response = await this.client.get('/admin/sso/status');
    return response.data;
  }

  async configureMsEntra(config: {
    tenantId: string;
    clientId: string;
    clientSecret?: string;
    samlMetadataUrl?: string;
  }): Promise<{ success: boolean; message: string }> {
    // Convert camelCase to snake_case for backend
    const payload = {
      tenant_id: config.tenantId,
      client_id: config.clientId,
      client_secret: config.clientSecret,
      saml_metadata_url: config.samlMetadataUrl,
    };
    const response = await this.client.put('/admin/sso/microsoft-entra', payload);
    return response.data;
  }

  async configureGoogleWorkspace(config: {
    clientId?: string;
    clientSecret?: string;
    domain: string;
    samlMetadataUrl?: string;
    serviceAccountJson?: string;
    adminEmail?: string;
  }): Promise<{ success: boolean; message: string }> {
    // Convert camelCase to snake_case for backend
    const payload = {
      domain: config.domain,
      saml_metadata_url: config.samlMetadataUrl,
      service_account_json: config.serviceAccountJson,
      admin_email: config.adminEmail,
    };
    const response = await this.client.put('/admin/sso/google-workspace', payload);
    return response.data;
  }

  async activateSso(provider: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/admin/sso/activate', { provider });
    return response.data;
  }

  async deactivateSso(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/admin/sso/deactivate');
    return response.data;
  }

  async fetchEntraGroups(config: {
    tenantId: string;
    clientId: string;
    clientSecret: string;
  }): Promise<{ groups: Array<{ id: string; name: string; description: string | null }> }> {
    // Convert camelCase to snake_case for backend
    const payload = {
      tenant_id: config.tenantId,
      client_id: config.clientId,
      client_secret: config.clientSecret,
    };
    const response = await this.client.post('/admin/sso/microsoft-entra/groups', payload);
    return response.data;
  }

  async fetchGoogleGroups(config: {
    serviceAccountJson: string;
    domain: string;
    adminEmail: string;
  }): Promise<{ groups: Array<{ id: string; name: string; email?: string; description: string | null }> }> {
    // Convert camelCase to snake_case for backend
    const payload = {
      service_account_json: config.serviceAccountJson,
      domain: config.domain,
      admin_email: config.adminEmail,
    };
    const response = await this.client.post('/admin/sso/google-workspace/groups', payload);
    return response.data;
  }

  async getRoleMappings(provider: string): Promise<{
    admin_group_ids: string[];
    leadership_group_ids: string[];
    product_team_group_ids: string[];
    user_group_ids: string[];
  }> {
    const response = await this.client.get(`/admin/sso/${provider}/role-mappings`);
    return response.data;
  }

  async updateRoleMappings(
    provider: string,
    mappings: {
      admin_group_ids: string[];
      leadership_group_ids: string[];
      product_team_group_ids: string[];
      user_group_ids: string[];
    }
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.client.put(`/admin/sso/${provider}/role-mappings`, mappings);
    return response.data;
  }

  // =============================================================================
  // Support Ticket Endpoints (GitHub Issues)
  // =============================================================================

  async getSupportTickets(): Promise<SupportTicket[]> {
    const response = await this.client.get('/support/tickets');
    return response.data;
  }

  async getSupportTicket(number: number): Promise<SupportTicket> {
    const response = await this.client.get(`/support/tickets/${number}`);
    return response.data;
  }

  async createSupportTicket(data: SupportTicketCreate): Promise<SupportTicket> {
    const response = await this.client.post('/support/tickets', data);
    return response.data;
  }

  async getSupportTicketComments(number: number): Promise<any[]> {
    const response = await this.client.get(`/support/tickets/${number}/comments`);
    return response.data;
  }

  async addSupportTicketComment(number: number, body: string): Promise<any> {
    const response = await this.client.post(`/support/tickets/${number}/comments`, { body });
    return response.data;
  }

  // Knowledge base
  async getKnowledgeBaseSections(): Promise<any[]> {
    const response = await this.client.get('/support/knowledge-base');
    return response.data;
  }

  async getKnowledgeBaseArticle(sectionSlug: string, articleSlug: string): Promise<any> {
    const response = await this.client.get(`/support/knowledge-base/${sectionSlug}/${articleSlug}`);
    return response.data;
  }

  // =============================================================================
  // Statistics Endpoints
  // =============================================================================

  async getItemStats(): Promise<{
    total: number;
    by_type: Record<string, number>;
    by_status: Record<string, number>;
    by_priority: Record<string, number>;
  }> {
    const response = await this.client.get('/stats/items');
    return response.data;
  }

  async getRecentActivity(limit = 10): Promise<Item[]> {
    const response = await this.client.get(`/stats/recent-activity?limit=${limit}`);
    return response.data;
  }
}

export const apiClient = new ApiClient();
